import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2, Pencil, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { TemplateField, TemplateSection } from '@/lib/api'
import {
  countTemplate,
  formatDiffSummary,
  formatTemplateCount,
  isDiffEmpty,
  type GeneratedTemplate,
  type TemplateDiff,
} from '@/lib/templatePreview'

export type TemplatePreviewProps = {
  template: GeneratedTemplate
  diff?: TemplateDiff | null
  // Identifiant du snapshot (id d'invocation) : réinitialise les sections ouvertes à chaque génération.
  snapshotId?: string
  // Absent → pas de bouton de fermeture (cas du Sheet mobile qui a son propre ✕).
  onClose?: () => void
  // Donne le focus à la région de contenu au montage (réouverture au clavier).
  focusOnMount?: boolean
  // Incrémenté à chaque demande de réouverture : refocalise la région même si
  // `focusOnMount` était déjà vrai (preview repliée puis rouverte).
  focusRequest?: number
  className?: string
  // Story 6.4 — actions. Sans `onUse` ni `onEditManually`, l'en-tête reste celui de 6.3.
  status?: TemplatePreviewStatus
  onUse?: () => void
  onEditManually?: () => void
  actionPending?: 'use' | 'edit' | null
  manuallyEdited?: boolean
  // Édition manuelle bloquée (ex. réponse de l'agent en cours de stream).
  editDisabled?: boolean
}

export type TemplatePreviewStatus = 'draft' | 'active' | 'missing' | 'loading'

// Prévisualisation en lecture seule d'un template généré par l'agent (story 6.3).
export function TemplatePreview({
  template,
  diff = null,
  snapshotId,
  onClose,
  focusOnMount = false,
  focusRequest = 0,
  className,
  status = 'loading',
  onUse,
  onEditManually,
  actionPending = null,
  manuallyEdited = false,
  editDisabled = false,
}: TemplatePreviewProps) {
  const count = useMemo(() => formatTemplateCount(countTemplate(template)), [template])
  const hasActions = onUse !== undefined || onEditManually !== undefined
  const missing = status === 'missing'
  const regionRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (focusOnMount) regionRef.current?.focus()
  }, [focusOnMount, focusRequest])
  const hasChanges = !isDiffEmpty(diff)
  const summary = hasChanges && diff ? formatDiffSummary(diff) : null

  return (
    <section aria-label="Prévisualisation du template" className={cn('flex h-full min-h-0 flex-col bg-background', className)}>
      <div
        ref={regionRef}
        role="region"
        aria-label="Contenu du template"
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-500"
      >
        <header className="sticky top-0 z-10 border-b bg-background/95 px-4 py-3 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-slate-900">{template.name}</h2>
              <p className="text-xs text-muted-foreground" data-testid="template-preview-count">
                {count}
              </p>
              {manuallyEdited && (
                <p className="mt-0.5 text-xs text-slate-600" data-testid="template-preview-manual">
                  Modifié manuellement
                </p>
              )}
              {summary && (
                <p className="mt-0.5 text-xs text-amber-700" data-testid="template-preview-diff">
                  <span aria-hidden="true">✨ </span>
                  {summary}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {onClose && (
                <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Fermer la preview">
                  <X aria-hidden="true" className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {hasActions && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="template-preview-actions">
              {onUse && status === 'active' ? (
                <Badge variant="secondary" className="gap-1">
                  <Check aria-hidden="true" className="h-3 w-3" />
                  Dans la bibliothèque
                </Badge>
              ) : onUse ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={onUse}
                  disabled={status !== 'draft' || actionPending !== null}
                >
                  {actionPending === 'use' ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check aria-hidden="true" className="h-4 w-4" />
                  )}
                  Utiliser ce template
                </Button>
              ) : null}
              {onEditManually && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onEditManually}
                  disabled={(status !== 'draft' && status !== 'active') || actionPending !== null || editDisabled}
                >
                  {actionPending === 'edit' ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  ) : (
                    <Pencil aria-hidden="true" className="h-4 w-4" />
                  )}
                  Modifier manuellement
                </Button>
              )}
              {missing && (
                <p className="w-full text-xs text-muted-foreground" data-testid="template-preview-missing">
                  Ce brouillon n'est plus disponible
                </p>
              )}
            </div>
          )}
          {template.description && <p className="mt-2 text-sm text-slate-600">{template.description}</p>}
        </header>

        <div className="px-4 py-2">
          {template.sections.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">Ce template ne contient aucune section.</p>
          ) : (
            <SectionsAccordion
              key={snapshotId ?? template.templateId}
              sections={template.sections}
              diff={hasChanges ? diff : null}
            />
          )}
        </div>
      </div>
    </section>
  )
}

function initialOpen(sections: TemplateSection[], diff: TemplateDiff | null): string[] {
  if (diff && diff.changedSectionIds.size > 0) {
    const changed = sections.filter((s) => diff.changedSectionIds.has(s.id)).map((s) => s.id)
    if (changed.length > 0) return changed
  }
  return sections[0] ? [sections[0].id] : []
}

function SectionsAccordion({ sections, diff }: { sections: TemplateSection[]; diff: TemplateDiff | null }) {
  const [open, setOpen] = useState<string[]>(() => initialOpen(sections, diff))

  return (
    <Accordion type="multiple" value={open} onValueChange={setOpen}>
      {sections.map((section) => {
        const sectionChanged = diff?.changedSectionIds.has(section.id) ?? false
        const n = section.fields.length
        return (
          <AccordionItem key={section.id} value={section.id}>
            <AccordionTrigger className="py-3">
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{section.title}</span>
                {sectionChanged && (
                  <span role="img" aria-label="Section modifiée">
                    ✨
                  </span>
                )}
                <Badge variant="secondary">
                  {n} {n > 1 ? 'champs' : 'champ'}
                </Badge>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {n === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun champ.</p>
              ) : (
                <ul className="space-y-1.5">
                  {section.fields.map((field) => (
                    <FieldRow
                      key={field.id}
                      field={field}
                      change={diff?.added.has(field.id) ? 'added' : diff?.modified.has(field.id) ? 'modified' : null}
                    />
                  ))}
                </ul>
              )}
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}

function FieldRow({ field, change }: { field: TemplateField; change: 'added' | 'modified' | null }) {
  return (
    <li
      data-testid={`template-field-${field.id}`}
      className={cn('rounded-md px-2 py-1.5', change && 'bg-amber-50 ring-1 ring-amber-200')}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {change && (
          <span role="img" aria-label={change === 'added' ? 'Nouveau champ' : 'Champ modifié'}>
            ✨
          </span>
        )}
        <span className="text-sm font-medium text-slate-900">{field.label}</span>
        <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs text-slate-700">{field.type}</code>
        {field.unit && <span className="text-xs text-slate-600">({field.unit})</span>}
        {field.required && <span className="text-xs font-medium text-red-700">obligatoire</span>}
      </div>
      {field.description && <p className="mt-0.5 text-xs text-muted-foreground">{field.description}</p>}
      {field.type === 'select' && field.options && field.options.length > 0 && (
        <ul aria-label={`Options de ${field.label}`} className="mt-1 flex flex-wrap gap-1">
          {field.options.map((opt) => (
            <li key={opt} className="rounded border px-1.5 py-0.5 text-xs text-slate-700">
              {opt}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
